/**
 * SettingsGeneralSection
 *
 * General calendar settings: default view, week start, time format,
 * working hours, agenda lookahead, week numbers, and the weather overlay
 * with its temperature units.
 */

import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings } from 'lucide-react';
import type { CalendarSettings, CalendarView, TemperatureUnit, TimeFormat } from '../types';
import { Section, ToggleRow, Field, formatHour, HOURS } from './settings-helpers';

interface SettingsGeneralSectionProps {
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

export function SettingsGeneralSection({
  settings,
  onUpdateSettings,
}: SettingsGeneralSectionProps) {
  const tf = settings.timeFormat ?? '12h';

  return (
    <Section icon={Settings} title="General">
      <Field label="Default view">
        <Select
          value={settings.defaultView}
          onValueChange={(v) => onUpdateSettings({ defaultView: v as CalendarView })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="agenda">Agenda</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Week starts on">
        <Select
          value={String(settings.weekStartDay)}
          onValueChange={(v) => onUpdateSettings({ weekStartDay: parseInt(v, 10) as 0 | 1 })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Sunday</SelectItem>
            <SelectItem value="1">Monday</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Time format">
        <Select value={tf} onValueChange={(v) => onUpdateSettings({ timeFormat: v as TimeFormat })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="12h">12-hour (AM/PM)</SelectItem>
            <SelectItem value="24h">24-hour</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Working hours">
        <div className="flex items-center gap-2">
          <Select
            value={String(settings.workingHoursStart)}
            onValueChange={(v) => onUpdateSettings({ workingHoursStart: parseInt(v, 10) })}
          >
            <SelectTrigger className="w-[6.5rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {formatHour(h, tf)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">to</span>
          <Select
            value={String(settings.workingHoursEnd)}
            onValueChange={(v) => onUpdateSettings({ workingHoursEnd: parseInt(v, 10) })}
          >
            <SelectTrigger className="w-[6.5rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {formatHour(h, tf)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Field>

      <Field label="Agenda lookahead (days)">
        <Input
          type="number"
          min={7}
          max={365}
          value={settings.agendaDaysAhead}
          onChange={(e) =>
            onUpdateSettings({ agendaDaysAhead: parseInt(e.target.value, 10) || 30 })
          }
          className="w-24"
        />
      </Field>

      <label className="flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2.5">
        <Checkbox
          checked={settings.showWeekNumbers}
          onCheckedChange={(c) => onUpdateSettings({ showWeekNumbers: c === true })}
        />
        <span className="text-[13px]">Show week numbers</span>
      </label>

      <ToggleRow
        label="Weather overlay"
        checked={settings.showWeather ?? true}
        onCheckedChange={(checked) => onUpdateSettings({ showWeather: checked })}
      />

      {(settings.showWeather ?? true) && (
        <Field label="Temperature units">
          <Select
            value={settings.temperatureUnit}
            onValueChange={(v) => onUpdateSettings({ temperatureUnit: v as TemperatureUnit })}
          >
            <SelectTrigger className="w-[9rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
              <SelectItem value="celsius">Celsius (°C)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
    </Section>
  );
}
